import { useCallback, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import { usePreviewMode } from "datasets/hooks/usePreviewMode";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Dataset from "datasets/types/interfaces/dataset";
import {
  AlertCircle,
  BarChart3,
  BookOpen,
  Check,
  Files,
  KeyRound,
  LayoutDashboard,
  Sparkles,
  Table2,
  Trash2,
  Users,
  View,
} from "lucide-react";
import yn from "yn";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { DropdownMenuItem, DropdownMenuSeparator } from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import useToast from "~/components/ui/toast";
import useNextGenHeader from "~/contexts/nextgen-header";
import useUser from "~/contexts/user";
import useUserPermission from "~/hooks/useUserPermission";
import { buildEntityBreadcrumbs, getSectionSegment } from "~/pages/manifold-nextgen/breadcrumbs";
import { SectionNavLink } from "~/pages/manifold-nextgen/components/SectionNavLink";
import { SectionSidebar } from "~/pages/manifold-nextgen/components/SectionSidebar";
import { SettingsMenu } from "~/pages/manifold-nextgen/components/SettingsMenu";
import { ShareButton } from "~/pages/manifold-nextgen/components/share/ShareButton";
import { ShareModal } from "~/pages/manifold-nextgen/components/share/ShareModal";
import { useDatasetShare } from "~/pages/manifold-nextgen/hooks/useDatasetShare";
import { ROUTE_404, ROUTE_NEXTGEN_DATASETS, nextGenDatasetDetailPath } from "~/router/constants";
import { apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

const BASE_NAV_ITEMS = [
  {
    label: "Overview",
    path: "overview",
    icon: <LayoutDashboard className="size-4" />,
  },
  {
    label: "Dashboards",
    path: "dashboards",
    icon: <BarChart3 className="size-4" />,
  },
] as const;

// Section path → breadcrumb label for the trailing crumb. "overview" is the
// dataset root, already represented by the title crumb, so it is omitted here.
const SECTION_LABELS: Record<string, string> = {
  dashboards: "Dashboards",
  tables: "Tables",
  files: "Files",
  "ai-context": "Context",
  "ai-skills": "Skills",
  "cohort-explorer": "Cohort Explorer",
  "access-requests": "Access Requests",
};

export const DatasetDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { datasetId } = useParams<{ datasetId: string }>();
  const { data: dataset, error, isLoading, mutate } = useDataset();
  const canEdit = useDatasetPermission("edit:catalog-dataset");
  const canDelete = useDatasetPermission("delete:catalog-dataset") && canEdit;
  const hasCohortExplorerPermission = useUserPermission("view:cohort-explorer-config-app");
  const previewFeatureEnabled = usePreviewMode();
  const { showSuccess, showError } = useToast();
  const currentUser = useUser();
  const {
    collaborators,
    isLoading: collaboratorsLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  } = useDatasetShare();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isEnablingPreview, setIsEnablingPreview] = useState(false);

  const cohortExplorerEnabled =
    yn(import.meta.env.VITE_COHORT_EXPLORER_ENABLED, { default: false }) &&
    hasCohortExplorerPermission;

  const sectionSegment = getSectionSegment(location.pathname, datasetId);
  const sectionLabel = sectionSegment ? SECTION_LABELS[sectionSegment] : undefined;

  const headerBreadcrumbs = useMemo(
    () =>
      buildEntityBreadcrumbs({
        rootHref: ROUTE_NEXTGEN_DATASETS,
        entityName: dataset?.title,
        // Link the title back to the dataset root only when a deeper section is
        // showing, so it stays a plain label on the overview itself.
        entityHref: sectionLabel && datasetId ? nextGenDatasetDetailPath(datasetId) : undefined,
        sectionLabel,
      }),
    [dataset?.title, sectionLabel, datasetId]
  );

  const handleCopy = useCallback(
    async (text: string, label: string) => {
      await navigator.clipboard.writeText(text);
      showSuccess(`${label} copied`);
    },
    [showSuccess]
  );

  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;
  const headerActions = useMemo<[React.ReactNode?, React.ReactNode?]>(() => {
    if (!dataset) {
      return [];
    }
    const shareNode = canEdit ? (
      <ShareButton key="share-dataset" label="Share Dataset" onClick={() => setShareOpen(true)} />
    ) : undefined;

    const settingsNode = (
      <SettingsMenu key="dataset-settings">
        {canEdit && (
          <DropdownMenuItem
            onClick={() => {
              setRenameTitle(dataset.title);
              setRenameOpen(true);
            }}
          >
            Rename…
          </DropdownMenuItem>
        )}
        {canEdit && <DropdownMenuSeparator />}
        <DropdownMenuItem onClick={() => handleCopy(dataset.id, "ID")}>Copy ID</DropdownMenuItem>
        {dataset.slug && (
          <DropdownMenuItem onClick={() => handleCopy(dataset.slug!, "Slug")}>
            Copy slug
          </DropdownMenuItem>
        )}
        {previewFeatureEnabled && isOwner && <DropdownMenuSeparator />}
        {previewFeatureEnabled &&
          isOwner &&
          (dataset.previewEnabled ? (
            <DropdownMenuItem disabled>
              <Check />
              Preview Enabled
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => setPreviewOpen(true)}>
              <View />
              Enable Preview…
            </DropdownMenuItem>
          ))}
        {canDelete && <DropdownMenuSeparator />}
        {canDelete && (
          <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
            <Trash2 />
            Delete
          </DropdownMenuItem>
        )}
      </SettingsMenu>
    );

    return [shareNode, settingsNode];
  }, [dataset, canEdit, canDelete, previewFeatureEnabled, isOwner, handleCopy]);

  useNextGenHeader({ breadcrumbs: headerBreadcrumbs, actions: headerActions });

  const handleDelete = async () => {
    if (!dataset) return;
    setIsDeleting(true);
    try {
      await apiMutate(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        is_deleted: true,
      });
      showSuccess(`"${dataset.title}" deleted`);
      navigate(ROUTE_NEXTGEN_DATASETS, { replace: true });
    } catch {
      showError("Failed to delete dataset");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handleRename = async () => {
    if (!dataset) return;
    const trimmed = renameTitle.trim();
    if (!trimmed || trimmed === dataset.title) {
      setRenameOpen(false);
      return;
    }
    setIsRenaming(true);
    try {
      await apiMutate<Dataset>(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        title: trimmed,
      });
      mutate();
      setRenameOpen(false);
    } catch {
      showError("Failed to rename dataset");
    } finally {
      setIsRenaming(false);
    }
  };

  const handleEnablePreview = async () => {
    if (!dataset) return;
    setIsEnablingPreview(true);
    try {
      await apiMutate(
        `${VITE_CATALOG_API}/datasets/${dataset.id}/preview/enable`,
        "POST",
        undefined,
        { expectsText: true }
      );
      mutate((current) => (current ? { ...current, previewEnabled: true } : current), {
        revalidate: true,
      });
      showSuccess("Preview mode enabled");
      setPreviewOpen(false);
    } catch {
      showError("Unable to enable preview mode");
    } finally {
      setIsEnablingPreview(false);
    }
  };

  if (error && (error as { status?: number }).status === 404) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading dataset" className="flex min-h-full">
        <div className="w-56 shrink-0 border-r border-border px-4 py-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="mx-1 my-1 h-8 rounded-md" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <Skeleton className="h-64 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !dataset) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load dataset</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <SectionSidebar ariaLabel="Dataset sections">
          <div className="flex flex-col gap-1 px-4 py-3">
            {BASE_NAV_ITEMS.map(({ label, path, icon }) => (
              <SectionNavLink key={path} to={path} end icon={icon} label={label} />
            ))}
            {dataset.access_level !== DatasetAccessLevel.PREVIEW && (
              <SectionNavLink
                to="tables"
                end
                icon={<Table2 className="size-4" />}
                label="Tables"
                badge={dataset.table_count}
              />
            )}
            {dataset.access_level !== DatasetAccessLevel.PREVIEW && (
              <SectionNavLink to="files" icon={<Files className="size-4" />} label="Files" />
            )}
            {dataset.aiAgentsEnabled && dataset.access_level !== DatasetAccessLevel.PREVIEW && (
              <SectionNavLink
                to="ai-context"
                icon={<BookOpen className="size-4" />}
                label="Context"
              />
            )}
            {dataset.aiAgentsEnabled && dataset.access_level !== DatasetAccessLevel.PREVIEW && (
              <SectionNavLink
                to="ai-skills"
                icon={<Sparkles className="size-4" />}
                label="Skills"
              />
            )}
            {cohortExplorerEnabled && dataset.access_level === DatasetAccessLevel.OWNER && (
              <SectionNavLink
                to="cohort-explorer"
                icon={<Users className="size-4" />}
                label="Cohort Explorer"
              />
            )}
            {cohortExplorerEnabled && dataset.access_level === DatasetAccessLevel.OWNER && (
              <SectionNavLink
                to="access-requests"
                icon={<KeyRound className="size-4" />}
                label="Access Requests"
              />
            )}
          </div>
        </SectionSidebar>

        <div className="flex-1 overflow-y-auto p-6">
          <Outlet context={dataset} />
        </div>
      </div>

      <Dialog
        open={renameOpen}
        onOpenChange={(open) => {
          if (!open) setRenameOpen(false);
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Rename Dataset</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rename-dataset">Name</Label>
            <Input
              id="rename-dataset"
              value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRename()}
              disabled={isRenaming}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)} disabled={isRenaming}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={isRenaming || !renameTitle.trim()}>
              {isRenaming ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{dataset.title}&rdquo;</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this dataset?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable Preview Mode</AlertDialogTitle>
            <AlertDialogDescription>
              You are enabling preview mode for &ldquo;{dataset.title}&rdquo;. This allows the
              dataset to be shared with preview access, where users see metadata only, not
              underlying data. This setting cannot be disabled without administrator assistance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isEnablingPreview}>Cancel</AlertDialogCancel>
            <Button onClick={handleEnablePreview} disabled={isEnablingPreview}>
              {isEnablingPreview ? "Enabling…" : "Enable"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        resourceLabel="Dataset"
        resourceName={dataset.title}
        collaborators={collaborators}
        collaboratorsLoading={collaboratorsLoading}
        collaboratorsError={collaboratorsError}
        accessLevels={accessLevels}
        defaultAccessLevel={defaultAccessLevel}
        onShare={handleShare}
        currentUserEmail={currentUser?.email}
      />
    </>
  );
};
